import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import TopNav from "../components/TopNav";
import Footer from "../components/Footer";
import API from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { useAuth } from "../contexts/AuthContext";
import { Search, ArrowRight, AlertCircle, CheckCircle, Clock, Users } from "lucide-react";
import logger from "../lib/logger";

export function StaffDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [queue, setQueue] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [d, q] = await Promise.all([
          API.get("/dashboard"),
          API.get("/submissions", { params: { status: "submitted" } }),
        ]);
        setStats(d.data); setQueue((q.data.items || []).slice(0, 6));
      } catch (e) { logger.error("Staff dashboard load failed:", e); }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <TopNav />
      <div className="max-w-6xl mx-auto px-6 py-10 flex-1 w-full">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-widest text-muted-h mb-2">Heritage Team</div>
          <h1 className="font-display text-4xl text-ink">Staff Dashboard</h1>
          <p className="text-ink-soft mt-1">Welcome back, {user?.first_name}.</p>
        </div>

        {/* Needs Your Attention */}
        <section className="mb-8">
          <h2 className="font-display text-2xl text-ink mb-4 flex items-center gap-2"><AlertCircle size={20} className="text-terracotta" /> Needs Your Attention</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat label="New Submissions" v={stats.new_submissions} link="/staff/submissions?status=submitted" accent testid="staff-stat-new" />
            <Stat label="Awaiting Validation" v={stats.awaiting_validation} link="/staff/submissions?status=validation_requested" testid="staff-stat-val" />
            <Stat label="Validated · Decide" v={stats.validated_pending_decision} link="/staff/submissions?status=ready_for_approval" testid="staff-stat-decide" />
            <Stat label="Assistance Requests" v={stats.assistance_requests} link="/staff/tasks?type=assistance_request" testid="staff-stat-assist" />
          </div>
        </section>

        <section className="mb-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat label="Under Review" v={stats.under_review} link="/staff/submissions?status=under_review" testid="staff-stat-under" />
            <Stat label="Info Requested" v={stats.info_requested} link="/staff/submissions?status=more_info_requested" testid="staff-stat-info" />
            <Stat label="Approved · Unpublished" v={stats.approved_unpublished} link="/staff/submissions?status=approved" testid="staff-stat-approved" />
            <Stat label="Published" v={stats.published} link="/staff/submissions?status=published" testid="staff-stat-pub" />
          </div>
        </section>

        <section>
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-display text-2xl text-ink">New Submissions</h2>
            <Link to="/staff/submissions" className="text-sm text-terracotta hover:underline">Open Queue →</Link>
          </div>
          <div className="card-heritage p-0 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[var(--heritage-parchment)] text-left text-xs uppercase tracking-wider text-muted-h">
                <tr>
                  <th className="px-4 py-3">Item</th>
                  <th className="px-4 py-3">Contributor</th>
                  <th className="px-4 py-3">Submitted</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {queue.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-h">No new submissions.</td></tr>
                )}
                {queue.map((s) => (
                  <tr key={s.id} className="border-t border-heritage hover:bg-[var(--heritage-cream)]" data-testid={`staff-queue-row-${s.id}`}>
                    <td className="px-4 py-3 text-ink font-medium">{s.item?.title || "Untitled"}</td>
                    <td className="px-4 py-3 text-ink-soft">{s.owner_snapshot?.first_name} {s.owner_snapshot?.last_name}</td>
                    <td className="px-4 py-3 text-muted-h">{s.submitted_at ? new Date(s.submitted_at).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-3 text-right"><Link to={`/staff/submissions/${s.id}`} className="text-terracotta hover:underline">Review <ArrowRight size={14} className="inline" /></Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
}

export function SubmissionQueue() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState(new URLSearchParams(window.location.search).get("status") || "");

  const load = async () => {
    const { data } = await API.get("/submissions", { params: { q: q || undefined, status: statusFilter || undefined } });
    setItems(data.items || []);
  };
  useEffect(() => { load(); }, [statusFilter]);

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <TopNav />
      <div className="max-w-7xl mx-auto px-6 py-10 flex-1 w-full">
        <h1 className="font-display text-4xl text-ink mb-6">Submission Queue</h1>
        <div className="card-heritage mb-4 flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-h" />
            <input type="search" placeholder="Search title, contributor…" className="input-heritage pl-9"
                   value={q} onChange={(e) => setQ(e.target.value)}
                   onKeyDown={(e) => e.key === "Enter" && load()} data-testid="queue-search" />
          </div>
          <select className="input-heritage max-w-xs" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="queue-status-filter">
            <option value="">All Statuses</option>
            <option value="submitted">Submitted</option>
            <option value="under_review">Under Review</option>
            <option value="more_info_requested">Info Requested</option>
            <option value="validation_requested">Validation Requested</option>
            <option value="validation_in_progress">Validation In Progress</option>
            <option value="validated">Validated</option>
            <option value="ready_for_approval">Ready for Approval</option>
            <option value="approved">Approved</option>
            <option value="denied">Denied</option>
            <option value="published">Published</option>
          </select>
        </div>
        <div className="card-heritage p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[var(--heritage-parchment)] text-left text-xs uppercase tracking-wider text-muted-h">
              <tr>
                <th className="px-4 py-3">ID</th>
                <th className="px-4 py-3">Item</th>
                <th className="px-4 py-3">Contributor</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Updated</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-h">No submissions match your filters.</td></tr>
              )}
              {items.map((s) => (
                <tr key={s.id} className="border-t border-heritage hover:bg-[var(--heritage-cream)]" data-testid={`queue-row-${s.id}`}>
                  <td className="px-4 py-3 font-mono text-xs text-muted-h">{s.id.slice(0, 8)}</td>
                  <td className="px-4 py-3 text-ink font-medium">{s.item?.title || "Untitled"}</td>
                  <td className="px-4 py-3 text-ink-soft">{s.owner_snapshot?.first_name} {s.owner_snapshot?.last_name}</td>
                  <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
                  <td className="px-4 py-3 text-muted-h">{new Date(s.updated_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-right"><Link to={`/staff/submissions/${s.id}`} className="text-terracotta hover:underline">Open →</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export function TasksPage() {
  const [items, setItems] = useState([]);
  const { user } = useAuth();
  useEffect(() => { (async () => {
    try {
      const { data } = await API.get("/tasks", { params: user?.role === "staff" || user?.role === "admin" ? {} : { mine: true } });
      setItems(data.items || []);
    } catch (e) { logger.error("Tasks load failed:", e); }
  })(); }, [user]);

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <TopNav />
      <div className="max-w-6xl mx-auto px-6 py-10 flex-1 w-full">
        <h1 className="font-display text-4xl text-ink mb-6">Tasks</h1>
        <div className="card-heritage p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[var(--heritage-parchment)] text-left text-xs uppercase tracking-wider text-muted-h">
              <tr><th className="px-4 py-3">Type</th><th className="px-4 py-3">Assigned To</th><th className="px-4 py-3">Notes</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Created</th><th className="px-4 py-3"></th></tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-h">No tasks.</td></tr>}
              {items.map((t) => (
                <tr key={t.id} className="border-t border-heritage" data-testid={`task-row-${t.id}`}>
                  <td className="px-4 py-3 text-ink">{t.task_type.replace(/_/g, " ")}</td>
                  <td className="px-4 py-3 text-ink-soft">{t.assigned_to_name || t.assigned_to?.slice(0, 8)}</td>
                  <td className="px-4 py-3 text-ink-soft line-clamp-2 max-w-sm">{t.notes}</td>
                  <td className="px-4 py-3"><span className={`badge ${t.status === "completed" ? "badge-approved" : "badge-review"}`}>{t.status}</span></td>
                  <td className="px-4 py-3 text-muted-h">{new Date(t.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-right">{t.submission_id && <Link to={`/staff/submissions/${t.submission_id}`} className="text-terracotta hover:underline">Open →</Link>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function Stat({ label, v, link, accent, testid }) {
  const val = v ?? 0;
  return (
    <Link to={link} className={`card-heritage hover:shadow-md hover:-translate-y-0.5 transition ${accent && val > 0 ? "!border-[var(--heritage-terracotta)]" : ""}`} data-testid={testid}>
      <div className="text-xs uppercase tracking-widest text-muted-h">{label}</div>
      <div className={`font-display text-4xl mt-2 ${accent && val > 0 ? "text-terracotta" : "text-ink"}`}>{val}</div>
    </Link>
  );
}
